import CheckIcon from "@mui/icons-material/Check";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import Typography from "@mui/material/Typography";
import type { MRT_Column, MRT_RowData, MRT_TableInstance } from "material-react-table";
import { columnGroupings, columnLabel, isDataColumn, sortDirectionLabels } from "_shared/_views/tables/table-card-layout";
import { SheetHeading, sheetPaperSx } from "_shared/_views/tables/table-sheet";

/**
 * Every column the table can sort by, hidden or shown. On the desktop table a hidden column can still
 * be sorted by from another column's menu (the proposals Dates column sorts by its three dates), so a
 * phone lists them all: nothing the table offers is out of reach (#6715).
 */
export function sortableColumns<TData extends MRT_RowData>(table: MRT_TableInstance<TData>): MRT_Column<TData>[] {
  return table.getAllLeafColumns().filter((column) => isDataColumn(column) && column.getCanSort());
}

/** What the table is sorted by, in words: "Name, A to Z". Undefined when it keeps its own order. */
export function currentSortLabel<TData extends MRT_RowData>(table: MRT_TableInstance<TData>): string | undefined {
  const [first] = table.getState().sorting;
  if (!first) return undefined;
  const column = table.getAllLeafColumns().find((candidate) => candidate.id === first.id);
  if (!column) return undefined;
  const labels = sortDirectionLabels(column);
  return `${columnLabel(column)}, ${first.desc ? labels.descending : labels.ascending}`;
}

/**
 * The phone's Sort: a bottom sheet with each sortable column in both directions (#6715). A pick sorts
 * the list and closes the sheet; "Default order" hands the order back to the list, where the table lets its
 * sort be removed (`enableSortingRemoval`). Where a column offers
 * a grouping (`meta.group`), the sheet lists it first, under Group, and is titled Sort and group: on the
 * desktop the grouping is in the column's menu, which a card does not have.
 */
export function TableSortSheet<TData extends MRT_RowData>(
  props: Readonly<{ table: MRT_TableInstance<TData>; open: boolean; onClose: () => void }>,
) {
  "use no memo";

  const { table, open, onClose } = props;
  const [current] = table.getState().sorting;
  const groupings = columnGroupings(table.getAllLeafColumns() as MRT_Column<TData>[]);
  const title = groupings.length > 0 ? "Sort and group" : "Sort";
  // The Sort icon has no room to name the order, so the sheet says it first.
  const sortLabel = currentSortLabel(table);

  function sortBy(id: string | undefined, desc: boolean) {
    table.setSorting(id === undefined ? [] : [{ id, desc }]);
    onClose();
  }

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: sheetPaperSx,
          "aria-labelledby": "table-sort-sheet-title",
          "aria-describedby": "table-sort-sheet-current",
        },
      }}
    >
      <SheetHeading id="table-sort-sheet-title" title={title} onClose={onClose} />
      <Typography id="table-sort-sheet-current" variant="body2" color="text.secondary" sx={{ paddingInline: 2 }}>
        {sortLabel ? `Sorted by ${sortLabel}` : "In the default order"}
      </Typography>
      <List dense>
        {groupings.length > 0 && (
          <li>
            <List dense disablePadding>
              <ListSubheader disableSticky>Group</ListSubheader>
              {groupings.map((grouping) => (
                <ListItemButton
                  key={grouping.label}
                  selected={grouping.isGrouped}
                  aria-pressed={grouping.isGrouped}
                  onClick={() => {
                    grouping.onToggle();
                    onClose();
                  }}
                >
                  <ListItemIcon>{grouping.isGrouped && <CheckIcon fontSize="small" />}</ListItemIcon>
                  <ListItemText>{grouping.label}</ListItemText>
                </ListItemButton>
              ))}
            </List>
          </li>
        )}
        {groupings.length > 0 && <ListSubheader disableSticky>Sort</ListSubheader>}
        {table.options.enableSortingRemoval !== false && (
          <ListItemButton selected={!current} onClick={() => sortBy(undefined, false)}>
            <ListItemIcon>{!current && <CheckIcon fontSize="small" />}</ListItemIcon>
            <ListItemText>Default order</ListItemText>
          </ListItemButton>
        )}
        {sortableColumns(table).map((column) => {
          const labels = sortDirectionLabels(column);
          const label = columnLabel(column);
          return (
            <li key={column.id}>
              <List dense disablePadding>
                <ListSubheader disableSticky>{label}</ListSubheader>
                {[false, true].map((desc) => {
                  const isCurrent = current?.id === column.id && current.desc === desc;
                  const direction = desc ? labels.descending : labels.ascending;
                  return (
                    <ListItemButton
                      key={String(desc)}
                      selected={isCurrent}
                      aria-label={`${label}, ${direction}`}
                      onClick={() => sortBy(column.id, desc)}
                    >
                      <ListItemIcon>{isCurrent && <CheckIcon fontSize="small" />}</ListItemIcon>
                      <ListItemText>{direction}</ListItemText>
                    </ListItemButton>
                  );
                })}
              </List>
            </li>
          );
        })}
      </List>
    </Drawer>
  );
}
